import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AdminShell } from '@/components/admin-shell';
import { FgCard } from '@/components/fg-card';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToggle } from '@/components/fg-toggle';
import { FgToast } from '@/components/fg-toast';
import { FgDialog } from '@/components/fg-dialog';
import { SortableList, DragHandle } from '@/components/sortable-list';
import { api } from '@/lib/api';

interface Level {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  orderIndex: number;
  isFreePreview: boolean;
  newlyAddedAt: string | null;
  lessonCount: number;
}

interface LevelForm {
  name: string;
  displayName: string;
  description: string;
  orderIndex: number;
  isFreePreview: boolean;
  newlyAdded: boolean;
}

const EMPTY: LevelForm = {
  name: 'A1.1',
  displayName: 'Beginner German — Part 1',
  description: 'Your first lesson is free.',
  orderIndex: 1,
  isFreePreview: false,
  newlyAdded: false,
};

export function LevelsPage() {
  const qc = useQueryClient();
  const levels = useQuery<Level[]>({
    queryKey: ['levels'],
    queryFn: async () => (await api.get('/api/levels')).data,
  });
  const [editing, setEditing] = useState<Level | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Level | null>(null);
  const [optimisticOrder, setOptimisticOrder] = useState<Level[] | null>(null);

  useEffect(() => {
    setOptimisticOrder(null);
  }, [levels.data]);

  const displayed = optimisticOrder ?? levels.data ?? [];

  // PATCH body — the form's `newlyAdded: boolean` translates to the
  // backend's `newlyAddedAt: ISOString | null` (nullable timestamp). Inline
  // row-toggles send `newlyAddedAt` directly.
  type LevelPatch = Partial<Omit<LevelForm, "newlyAdded">> & {
    newlyAddedAt?: string | null;
  };

  const createLevel = useMutation({
    mutationFn: async (body: LevelForm) => {
      // Map form's `newlyAdded: boolean` → backend's `newlyAddedAt`
      const payload: Record<string, unknown> = { ...body };
      payload.newlyAddedAt = body.newlyAdded ? new Date().toISOString() : null;
      delete payload.newlyAdded;
      return (await api.post('/api/levels', payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels'] });
      FgToast.success('Level created');
      setCreating(false);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Create failed'),
  });

  const updateLevel = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: LevelPatch }) =>
      (await api.patch(`/api/levels/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels'] });
      FgToast.success('Level updated');
      setEditing(null);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Update failed'),
  });

  const deleteLevel = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/levels/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels'] });
      FgToast.success('Level deleted');
      setDeleting(null);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Delete failed'),
  });

  const reorderLevels = useMutation({
    mutationFn: async (ordered: { id: string; orderIndex: number }[]) =>
      (await api.post('/api/levels/reorder', ordered)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels'] });
      setOptimisticOrder(null);
    },
    onError: (e) => {
      FgToast.error((e as { message?: string }).message ?? 'Reorder failed');
      setOptimisticOrder(null);
    },
  });

  function persistLevelReorder(next: Level[]) {
    setOptimisticOrder(next);
    reorderLevels.mutate(next.map((l, i) => ({ id: l.id, orderIndex: l.orderIndex ?? i + 1 })));
  }

  const toggleFree = (l: Level, value: boolean) =>
    updateLevel.mutate({ id: l.id, body: { isFreePreview: value } });

  /**
   * Toggle the "newly added" flag that powers the landing-page banner.
   * Backend stores a nullable `newly_added_at` timestamp — non-null means
   * "newly added", null means "not". Sending `null` when toggling off
   * explicitly clears the timestamp.
   */
  const toggleNew = (l: Level, value: boolean) =>
    updateLevel.mutate({
      id: l.id,
      body: { newlyAddedAt: value ? new Date().toISOString() : null },
    });

  return (
    <AdminShell>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <FgCard className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold">Levels</h2>
            <span className="text-xs text-[#8E8E93]">{displayed.length} total</span>
          </div>
          {displayed.length === 0 ? (
            <p className="rounded-md bg-white/5 p-4 text-sm text-[#8E8E93] border border-[rgba(255,255,255,0.08)]">
              No levels yet — create your first one in the panel on the right.
            </p>
          ) : (
            <SortableList
              items={displayed}
              onReorder={(next) => persistLevelReorder(next)}
              renderItem={(l, handleProps) => (
                <FgCard className="flex items-center justify-between p-4 fg-anim-fast">
                  <div className="flex items-center gap-2">
                    <DragHandle
                      listeners={handleProps.listeners}
                      attributes={handleProps.attributes}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-[#FFD60A] px-2 py-0.5 text-xs font-bold text-[#0A0A0A]">
                          {l.name}
                        </span>
                        <span className="text-sm font-semibold text-[#FAFAFA]">{l.displayName}</span>
                        <span className="text-xs text-[#8E8E93]">{l.lessonCount} lessons</span>
                        {l.newlyAddedAt && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#FFD60A] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-[#0A0A0A]"
                            title="Surfaced on the landing page as the latest new drop."
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
                            New
                          </span>
                        )}
                        {reorderLevels.isPending && (
                          <span className="rounded bg-[#FF9F0A]/15 px-2 py-0.5 text-[10px] font-bold text-[#FF9F0A]">
                            SAVING…
                          </span>
                        )}
                      </div>
                      {l.description && (
                        <p className="mt-1 text-xs text-[#8E8E93]">{l.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <FgToggle
                      label="New drop"
                      checked={!!l.newlyAddedAt}
                      onChange={(v) => toggleNew(l, v)}
                      disabled={updateLevel.isPending}
                    />
                    <FgToggle
                      label="Free preview"
                      checked={l.isFreePreview}
                      onChange={(v) => toggleFree(l, v)}
                      disabled={updateLevel.isPending}
                    />
                    <FgButton size="sm" variant="outline" onClick={() => setEditing(l)}>
                      <Pencil size={14} /> Edit
                    </FgButton>
                    <FgButton
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleting(l)}
                      aria-label={`Delete ${l.displayName}`}
                    >
                      <Trash2 size={14} />
                    </FgButton>
                  </div>
                </FgCard>
              )}
            />
          )}
        </FgCard>

        <FgCard className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold">Create a level</h2>
            <FgButton size="sm" variant="outline" onClick={() => setCreating(true)}>
              <Plus size={14} /> Open form
            </FgButton>
          </div>
          <p className="text-xs text-[#8E8E93]">
            Quick-create defaults. Click <em>Open form</em> to customise — name + display name
            are required, the rest is optional.
          </p>
        </FgCard>
      </div>

      {(creating || editing) && (
        <LevelDialog
          level={editing}
          busy={createLevel.isPending || updateLevel.isPending}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={(form) => {
            if (editing) updateLevel.mutate({ id: editing.id, body: form });
            else createLevel.mutate(form);
          }}
        />
      )}

      {deleting && (
        <FgDialog
          open
          tone="danger"
          title={`Delete ${deleting.name}?`}
          description={
            deleting.lessonCount > 0
              ? `This will also delete all ${deleting.lessonCount} lesson${
                  deleting.lessonCount === 1 ? '' : 's'
                } inside it. This cannot be undone (on disk files in storage/videos/hls/${deleting.id}/ will leak — a janitor job is Phase 9 work).`
              : 'The level will be removed from the curriculum. This cannot be undone.'
          }
          onClose={() => setDeleting(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="primary"
                loading={deleteLevel.isPending}
                onClick={() => deleteLevel.mutate(deleting.id)}
              >
                <Trash2 size={14} /> Delete
              </FgButton>
            </>
          }
        >
          {deleting.lessonCount > 0 && (
            <div className="rounded-md border border-[#FF9F0A]/30 bg-[#FF9F0A]/10 p-3 text-sm text-[#FF9F0A]">
              ⚠ {deleting.lessonCount} lesson{deleting.lessonCount === 1 ? '' : 's'} will
              also be deleted (cascade).
            </div>
          )}
        </FgDialog>
      )}
    </AdminShell>
  );
}

function LevelDialog({
  level,
  busy,
  onClose,
  onSubmit,
}: {
  level: Level | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (form: LevelForm) => void;
}) {
  const [form, setForm] = useState<LevelForm>(
    level
      ? {
          name: level.name,
          displayName: level.displayName,
          description: level.description ?? '',
          orderIndex: level.orderIndex,
          isFreePreview: level.isFreePreview,
          newlyAdded: !!level.newlyAddedAt,
        }
      : EMPTY,
  );

  return (
    <FgDialog
      open
      size="md"
      title={level ? `Edit ${level.name}` : 'Create a level'}
      description={
        level
          ? 'Renaming is allowed. Lesson #1 of this level is auto-marked free preview (per DNA convention).'
          : 'Use a code like A1.1, A1.2, A2.1. The first lesson of a new level should be flagged free preview below.'
      }
      onClose={onClose}
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton onClick={() => onSubmit(form)} loading={busy}>
            {level ? 'Save changes' : 'Create level'}
          </FgButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
      >
        <FgInput
          label="Code (e.g. A1.1)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
        <FgInput
          label="Display name"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          required
        />
        <FgInput
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <FgInput
          label="Order index"
          type="number"
          value={String(form.orderIndex)}
          onChange={(e) => setForm({ ...form, orderIndex: Number(e.target.value) })}
        />
        <FgToggle
          label="Entire level is free preview"
          checked={form.isFreePreview}
          onChange={(v) => setForm({ ...form, isFreePreview: v })}
        />
        <FgToggle
          label="Mark as newly added (surfaces on landing)"
          checked={form.newlyAdded}
          onChange={(v) => setForm({ ...form, newlyAdded: v })}
        />
      </form>
    </FgDialog>
  );
}
